import {
  AUTH_COOKIE_AUTHENTICATION_MODE_KEY,
  AUTH_COOKIE_IMPERSONATE_TOKEN_KEY,
  AUTH_COOKIE_INTERNAL_TOKEN_KEY,
  AUTH_COOKIE_PUNCHOUT_TOKEN_KEY,
} from '@/feature/auth/services/auth.constant'

type CookiesAsRecord = Record<string, string | undefined>
type HeadersAsRecord = Record<string, string | undefined>
type HeaderTuple = [string, string | undefined]

/**
 * Builds a headers object from a list of conditional [key, value] pairs.
 * Any entry that is `false` (condition not met) or has an empty/undefined
 * value is dropped.
 *
 * @example:
 * ```ts
 * const headers = buildHeaders([
 *   !!aTruthyValue && ['x-some-header', someValue],
 *   !!aFalsyValue && ['x-a-falsy-header', aFalsyValue], // dropped
 * ])
 * // => { 'x-some-header': someValue }
 * ```
 */
const buildHeaders = (entries: Array<false | HeaderTuple>): HeadersAsRecord => {
  const validEntries = entries.filter(
    (entry): entry is HeaderTuple => entry !== false && !!entry[1],
  )

  return Object.fromEntries(validEntries)
}

/**
 * Extracts the `Authorization` header (and, when impersonating or internal, the
 * `x-internal-auth` header) from the auth-related cookies.
 *
 * Modes:
 * - Impersonation (`authenticationMode === 'impersonate'`): the impersonation
 *   token is sent as the Bearer token, and the internal token (if present) is
 *   sent separately as `x-internal-auth`.
 * - Internal (`authenticationMode === 'internal'`): the internal token is sent
 *   as `x-internal-auth`.
 * - Punchout (`authenticationMode === 'punchout'`): the punchout token is sent
 *   as the Bearer token.
 * - Standard: the regular auth token is sent as the Bearer token.
 */
export const extractAuthTokensHeadersFromCookies = (cookies: CookiesAsRecord): HeadersAsRecord => {
  const authToken = cookies['auth-token']
  const impersonateToken = cookies[AUTH_COOKIE_IMPERSONATE_TOKEN_KEY]
  const authenticationMode = cookies[AUTH_COOKIE_AUTHENTICATION_MODE_KEY]
  const internalToken = cookies[AUTH_COOKIE_INTERNAL_TOKEN_KEY]
  const punchoutToken = cookies[AUTH_COOKIE_PUNCHOUT_TOKEN_KEY]

  const isImpersonating = authenticationMode === 'impersonate'
  const isInternal = authenticationMode === 'internal'
  const isPunchout = authenticationMode === 'punchout'

  const effectiveToken = isPunchout ? punchoutToken : authToken || punchoutToken

  return buildHeaders([
    // Impersonation mode
    isImpersonating && ['Authorization', impersonateToken && `Bearer ${impersonateToken}`],
    isImpersonating && ['x-internal-auth', internalToken && `Bearer ${internalToken}`],
    // Internal mode
    isInternal && ['x-internal-auth', internalToken && `Bearer ${internalToken}`],
    // Punchout / Standard mode
    !isImpersonating &&
      !isInternal && ['Authorization', effectiveToken && `Bearer ${effectiveToken}`],
    !!authenticationMode && ['x-authentication-mode', authenticationMode],
  ])
}

/**
 * Extracts the account-scoping headers (company, point of sale, subsidiary,
 * and optionally pickup point of sale) used for multi-account / multi-POS support.
 */
const extractAccountScopeHeadersFromCookies = (cookies: CookiesAsRecord): HeadersAsRecord => {
  const companyCode = cookies['company-code']
  const pointOfSaleId = cookies['point-of-sale-id']
  const subsidiaryId = cookies['subsidiary-id']
  const pickupPointOfSaleId = cookies['pickup-point-of-sale-id']

  return buildHeaders([
    // Identifies a single customer company/account
    !!companyCode && ['x-company-code', companyCode],
    !!pointOfSaleId && ['x-point-of-sale-id', pointOfSaleId],
    !!subsidiaryId && ['x-subsidiary-id', subsidiaryId],

    // Convenience header for pickup point of sale in checkout
    !!pickupPointOfSaleId && ['x-pickup-point-of-sale-id', pickupPointOfSaleId],
  ])
}

/**
 * Utility for Apollo Client: extracts all authentication and account-scoping
 * headers derived from cookies.
 */
export const extractHeadersFromCookies = (cookies: CookiesAsRecord): HeadersAsRecord => ({
  ...extractAuthTokensHeadersFromCookies(cookies),
  ...extractAccountScopeHeadersFromCookies(cookies),
})
